import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import {
	AnnouncementGlobalSearchResultItem,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	searchAnnouncements,
	SearchIntegrationKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNilOrWhiteSpace,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	LazyLoadCursorKind,
	MediaQueryKind,
	RemoteDataSourceState,
	createRemoteDataSource,
	useAlerts,
	useUserTimezone,
	useValueEqualMemo,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	AnnouncementPreviewItemModel,
	AnnouncementSortProperty,
	AnnouncementSortingInformationModel,
	listAuthors
} from "client/Api/Hub/Announcements/announcementViewer";
import { SortDirection } from "client/Api/common";
import { never } from "client_legacy/ndaw/errors";
import {
	NoResults,
	SelectOption,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	getPropertySortText,
	MultiSelectOption
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ScopeLayout } from "../ScopeLayout";
import { SearchSettings } from "../SearchSettings";
import {
	HubItemCategoryModel,
	listCategories
} from "client/Api/Hub/hub";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { getComputedDisplayName } from "../../AddressBook/common";
import { SearchNoResults } from "../SearchNoResults";
import { checkHasFilter } from "./common";
import { AnnouncementItemView } from "../Items/AnnouncementItemView";
import { TimeZone } from "client/DateAndTime/timezones";
import { Guid } from "client_legacy/ndaw/guid";

export type AnnouncementSearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const AnnouncementSearch = (props: AnnouncementSearchProps): ReactElement => {
	const {
		searchTerm,
		mobileSearchSettingsTarget
	} = props;

	const [isLoadingFilters, setIsLoadingFilters] = useState<boolean>(false);
	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);
	const [formData, setFormData] = useState<AnnouncementSearchFormData>({
		...defaultFilters,
		sort: defaultSortValue
	});
	const [categoryOptions, setCategoryOptions] = useState<readonly MultiSelectOption<number>[]>(EMPTY_ARRAY);
	const [authorOptions, setAuthorOptions] = useState<readonly MultiSelectOption<number>[]>(EMPTY_ARRAY);

	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();

	const formHandler: IFormHandler<AnnouncementSearchFormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const {
		sort: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const listHandle: RefObject<ScrollableVirtualListHandle<AnnouncementGlobalSearchResultItem> | null> = useRef<ScrollableVirtualListHandle<AnnouncementGlobalSearchResultItem>>(null);

	const partialDataSourceOptions = useValueEqualMemo(
		(): Omit<Parameters<typeof useDataSource>[0], "desiredAmountOfItems"> => ({
			searchTerm: searchTerm
				.value,
			filtering: {
				authorIds: formData.authorIds,
				categoryIds: formData.categoryIds
			},
			sorting: formData.sort,
			globalContext: {
				searchVersion: searchTerm.version
			}
		}),
		[
			formData.authorIds,
			formData.categoryIds,
			formData.sort,
			searchTerm
		]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsLoadingFilters(true);
					const categoriesRequest = listCategories(HubItemKind.Announcement, signal);
					const authorsRequest = listAuthors(signal);

					const [categories, authors] = await Promise.all([categoriesRequest, authorsRequest]);

					setCategoryOptions(categories.map(convertCategoryToOption));
					setAuthorOptions(authors.map(convertAuthorToOption));
					setIsLoadingFilters(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsLoadingFilters(false);
					console.error(e);
				}

			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager]
	);

	const {
		currentItems,
		hasMoreOnServer,
		reload,
		getLoadingState
	} = useDataSource({
		...partialDataSourceOptions,
		desiredAmountOfItems
	});

	const loadMoreHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems((x) => x + PAGE_SIZE);
		},
		[]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(partialDataSourceOptions);
			setDesiredAmountOfItems(PAGE_SIZE);
			listHandle.current
				?.scrollToTop();
		},
		[partialDataSourceOptions]
	);

	const clearHandler = useCallback(
		(): void => {
			setFormData(
				(x) => ({
					...x,
					...defaultFilters
				})
			);

			formHandler.validation
				.triggerValueChanged();
		},
		[formHandler.validation]
	);

	const renderContent = (): ReactElement => {
		const loadingState: RemoteDataSourceState = getLoadingState();

		if (loadingState === "ready" && currentItems.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || currentItems.length > 0) {
			return (
				<ScrollableVirtualList
					items={currentItems}
					estimatedHeightOfAnItem={130}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="announcement-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={hasMoreOnServer}
								onRequestMoreItems={loadMoreHandler}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: AnnouncementGlobalSearchResultItem, index: number): ReactElement => (
						<AnnouncementItemView
							key={item.announcement.id}
							navigationIndex={index}
							item={item}
							isMobile={isMobile}
							itemScope={announcementScope}
							timezone={userTimezone}
						/>
					)}
				</ScrollableVirtualList>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Settings>
				<SearchSettings
					menuTarget={mobileSearchSettingsTarget}
					onClear={clearHandler}
					hasFilter={hasFilter}
				>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sort")}
						options={sortOptions}
						isExpandedByDefault
					/>
					<SearchSettings.MultiSelect
						label={localize("_Category")}
						attachable={formHandler.for("categoryIds")}
						options={categoryOptions}
						placeholder={localize("_SelectCategory(ies)")}
						hasBusyIndicator={isLoadingFilters}
					/>
					<SearchSettings.MultiSelect
						label={localize("_Author")}
						attachable={formHandler.for("authorIds")}
						options={authorOptions}
						placeholder={localize("_SelectAuthor(s)")}
						hasBusyIndicator={isLoadingFilters}
					/>
				</SearchSettings>

			</ScopeLayout.Settings>

			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
AnnouncementSearch.displayName = "AnnouncementSearch";

const PAGE_SIZE = 20;

type SortValue = {
	direction: Exclude<DataSourceSortDirection, DataSourceSortDirection.None>;
	propertyName: PropertyPath<AnnouncementGlobalSearchResultItem> & (
		| "announcement.viewsCount"
		| "announcement.publicationStart"
	);
};

type AnnouncementSearchFormData = {
	sort: SortValue;
	categoryIds: readonly number[];
	authorIds: readonly number[];
};

type FilterModel = Omit<AnnouncementSearchFormData, "sort">;

const defaultFilters: FilterModel = {
	authorIds: EMPTY_ARRAY,
	categoryIds: EMPTY_ARRAY
};

type FilterContext = {
	categoryIds: readonly number[];
	authorIds: readonly number[];
};

const announcementScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.Announcements
};

const useDataSource = createRemoteDataSource<
	AnnouncementGlobalSearchResultItem,
	FilterContext,
	// Trigger new request when searchVersion changes
	{ searchVersion: Guid; }
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.AfterLastItem
	})
	.getHook({
		performRequest(options): Promise<LazyLoadPageResult<AnnouncementGlobalSearchResultItem>> {
			if (isNilOrWhiteSpace(options.searchTerm)) {
				return Promise.resolve({items: [], hasMore: false});
			}

			const afterAnnouncement: AnnouncementPreviewItemModel | undefined = options.after
				?.announcement;

			return searchAnnouncements(
				{
					searchTerm: options.searchTerm,
					authorIds: options.filterState.authorIds,
					categoryIds: options.filterState.categoryIds,
					amount: options.amount,
					afterAnnouncement: isNotNil(afterAnnouncement)
						? {
							isSet: true,
							value: {
								id: afterAnnouncement.id,
								pinDate: afterAnnouncement.pinDate,
								startTime: afterAnnouncement.publicationStart,
								viewsCount: afterAnnouncement.viewsCount
							}
						}
						: { isSet: false },
					sortOrder: convertSort(
						ensureNotNil(options.sorting)
					)
				},
				options.signal
			);
		},
	});

function convertSort(
	sortingState: DataSourceSortableState<AnnouncementGlobalSearchResultItem>
): AnnouncementSortingInformationModel {
	if (!sortingState) {
		return {
			property: AnnouncementSortProperty.StartTime,
			direction: SortDirection.Descending
		};
	}

	if (sortingState.direction === DataSourceSortDirection.None) {
		throw new Error("sorting directions is not specified.");
	}

	let property: AnnouncementSortProperty;

	switch(sortingState.propertyName) {
		case "announcement.viewsCount":
			property = AnnouncementSortProperty.Views;
			break;
		case "announcement.publicationStart":
			property = AnnouncementSortProperty.StartTime;
			break;
		default:
			throw new Error(`Sorting on ${sortingState.propertyName} is not supported`);
	}

	const direction: SortDirection = sortingState.direction === DataSourceSortDirection.Ascending
		? SortDirection.Ascending
		: sortingState.direction === DataSourceSortDirection.Descending
			? SortDirection.Descending
			: never(sortingState.direction);

	return {
		property,
		direction
	};
}

function getUniqueKey(item: AnnouncementGlobalSearchResultItem): string {
	return `${item.announcement.id}`;
}

const defaultSortValue: SortValue = {
	propertyName: "announcement.publicationStart",
	direction: DataSourceSortDirection.Descending
};

const sortOptions: readonly SelectOption<SortValue>[] = [
	{
		text: getPropertySortText("_PublicationDate", "_NewToOld"),
		value: defaultSortValue,
		valueTrackingId: "publication-date-new-old"
	},
	{
		text: getPropertySortText("_PublicationDate", "_OldToNew"),
		value: { propertyName: "announcement.publicationStart", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "publication-date-old-new"
	},
	{
		text: localize("_MostPopular"),
		value: { propertyName: "announcement.viewsCount", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "most-popular"
	}
];

function convertCategoryToOption(category: HubItemCategoryModel): SelectOption<number> {
	return {
		text: category.name,
		value: category.id
	};
}

function convertAuthorToOption(author: UserIdentityModel): SelectOption<number> {
	const {
		computedDisplayName
	} = getComputedDisplayName(author);

	return {
		text: computedDisplayName,
		value: author.userId
	};
}